import { useEffect } from "react";

import { observer } from "mobx-react-lite";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";

import { AwarenessUsers } from "@/components/awareness/AwarenessUsers";
import { ChatWithTeamButton } from "@/components/chat/ChatWithTeamButton";
import { AlertDialog } from "@/components/dialog/alert/AlertDialog";
import { ShareDialog } from "@/components/dialog/share/ShareDialog";
import { ConnectionStatus } from "@/components/doc/right-sidebar/ConnectionStatus";
import { Icon } from "@/components/icons/Icon";
import { Sidebar } from "@/components/sidebar";
import { SidebarToolbar } from "@/components/sidebar/SidebarToolbar";
import { useStore } from "@/store";
import { useChats } from "@/store/useChats";
import { docPageUrl } from "@/urls";
import { trackEvent } from "@/utils/trackEvent";
import { twMerge } from "@/utils/twMerge";

import { DownloadMenu } from "./download-menu/DownloadMenu";
import { MarimoStatusBar } from "./MarimoStatusBar";
import { ShareButton } from "./share/ShareButton";

import type { DocumentModel } from "@/models/DocumentModel";

type Props = {
  doc: DocumentModel;
};

export const Menu = observer(function Menu({ doc }: Props) {
  const chats = useChats();
  const t = useTranslations();
  const { dialog, view } = useStore();
  const router = useRouter();

  useEffect(() => {
    if (!doc.permissions.comment) {
      return;
    }
    chats.fetchChatsWithDispose();
  }, [chats, doc.permissions.comment]);

  const handleForkButtonClick = async () => {
    if (!doc.id) {
      throw new Error("RightSidebarMenu.handleForkButtonClick: Document ID is required");
    }
    const forkedDocument = await dialog.forkDialogStore.handleForkButtonClick({
      docUidToFork: doc.id,
    });
    if (forkedDocument) {
      router.push(docPageUrl({ docId: forkedDocument.uid }));
      trackEvent("fork_document_clicked", { doc_id: doc.id, source: "bottom_panel" });
    }
  };

  const showMarimoStatusBar = doc.hasCodeBlock && doc.permissions.write;

  return (
    <>
      <div className="flex flex-1 flex-col min-w-0">
        <ConnectionStatus withBottomBorder visuallyHidden={view.isRightSidebarCollapsed} />
        {showMarimoStatusBar && (
          <MarimoStatusBar doc={doc} isCollapsed={view.isRightSidebarCollapsed} />
        )}
        <SidebarToolbar
          label={t("sidebar.documentActions")}
          vertical={view.isRightSidebarCollapsed}
          className={twMerge(
            "flex-1 justify-between min-h-[38px] items-center gap-2.5",
            showMarimoStatusBar && "mt-1.25",
          )}
        >
          <ChatWithTeamButton isCollapsed={view.isRightSidebarCollapsed} />
          <Sidebar.Button
            icon={<Icon name="Fork" />}
            title={t("sidebar.downloadMenu.fork")}
            onClick={handleForkButtonClick}
            className={twMerge("rounded-8 w-fit h-fit", "data-focus:bg-sidebar-button-active-bg")}
            hintKey="forkDocument"
          />
          <DownloadMenu doc={doc} isCollapsed={view.isRightSidebarCollapsed} />
          <div
            className={twMerge(
              "flex flex-1 gap-2.5 min-w-0 items-center justify-end self-stretch",
              view.isRightSidebarCollapsed && "flex-col",
            )}
          >
            <AwarenessUsers doc={doc} />
            <ShareButton />
          </div>
        </SidebarToolbar>
      </div>
      <ShareDialog doc={doc} />
      <AlertDialog
        isOpen={!!dialog.forkDialogStore.forkError}
        title={t("sidebar.downloadMenu.forkError")}
        description={dialog.forkDialogStore.forkError}
        onClose={() => {
          dialog.forkDialogStore.forkError = undefined;
        }}
      />
    </>
  );
});
